import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import StorageFactory from "../../Util/StorageFactory";
import { SESSION_USER_SUBSCRIPTION_TIER } from "../../Util/VMConstants";
import { isEmpty } from "../../Util/UtilHelper";
import {
  getUserNotFoundFromApiBody,
  getUserNotFoundFromAxiosError,
} from "../../Util/authSessionHelper";
import {
  checkTeamUserLimitTier,
  clearSubscriptionStatusTierCache,
  getUserSubscriptionStatusTier,
  parseSubscriptionFetchArg,
} from "../../Services/subscriptionTierService";
import { setUserNotFoundMessage } from "./subscriptionSlice";

const sessionFactory = new StorageFactory("session");

export const fetchSubscriptionTierStatus = createAsyncThunk(
  "subscriptionTier/fetchSubscriptionTierStatus",
  async (arg, { rejectWithValue, dispatch }) => {
    const { userID, forceRefresh } = parseSubscriptionFetchArg(arg);
    if (!userID) {
      return rejectWithValue("User ID is required");
    }

    try {
      const response = await getUserSubscriptionStatusTier(userID, { forceRefresh });

      const userNotFound = getUserNotFoundFromApiBody(response);
      if (userNotFound) {
        dispatch(setUserNotFoundMessage(userNotFound.message));
        return rejectWithValue(userNotFound);
      }

      if (response.status_code === 200) {
        const user = response.user || {};

        const userInfo = {
          id: user?.user_id || null,
          name: user?.name || "",
          email: user?.email || "",
          mobileNo: user?.mobile_no || "",
          userType: user?.user_type || "",
          profilePic: user?.image_url || "",
        };

        const permissions = !isEmpty(user?.permissions)
          ? user.permissions.map((p) => p.permission_id)
          : [];

        sessionFactory.add(SESSION_USER_SUBSCRIPTION_TIER, {
          plan: response.plan,
          plan_tier: response.plan_tier,
          plan_name: response.plan_name,
          is_paid: response.is_paid,
          is_subscription_active: response.is_subscription_active,
        });

        return {
          status: response,
          permissions,
          user: userInfo,
        };
      }

      return rejectWithValue(
        response.message || "Failed to fetch tier subscription status"
      );
    } catch (error) {
      const userNotFound = getUserNotFoundFromAxiosError(error);
      if (userNotFound) {
        dispatch(setUserNotFoundMessage(userNotFound.message));
        return rejectWithValue(userNotFound);
      }
      return rejectWithValue(error.message || "Network error");
    }
  }
);

export const fetchTeamUserLimitTier = createAsyncThunk(
  "subscriptionTier/fetchTeamUserLimitTier",
  async (userID, { rejectWithValue }) => {
    try {
      const response = await checkTeamUserLimitTier(userID);
      if (response.status_code === 200) {
        return response;
      }
      return rejectWithValue(response.message || "Failed to fetch team limit");
    } catch (error) {
      return rejectWithValue(error.message || "Network error");
    }
  }
);

const subscriptionTierSlice = createSlice({
  name: "subscriptionTier",
  initialState: {
    status: null,
    teamLimit: null,
    permissions: [],
    user: {
      id: null,
      name: "",
      email: "",
      mobileNo: "",
      userType: "",
      profilePic: "",
    },
    loading: false,
    teamLimitLoading: false,
    error: null,
  },
  reducers: {
    clearSubscriptionTier(state) {
      clearSubscriptionStatusTierCache();
      state.status = null;
      state.teamLimit = null;
      state.permissions = [];
      state.user = {
        id: null,
        name: "",
        email: "",
        mobileNo: "",
        userType: "",
        profilePic: "",
      };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscriptionTierStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionTierStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload.status;
        state.permissions = action.payload.permissions;
        state.user = action.payload.user;
      })
      .addCase(fetchSubscriptionTierStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTeamUserLimitTier.pending, (state) => {
        state.teamLimitLoading = true;
      })
      .addCase(fetchTeamUserLimitTier.fulfilled, (state, action) => {
        state.teamLimitLoading = false;
        state.teamLimit = action.payload;
      })
      .addCase(fetchTeamUserLimitTier.rejected, (state, action) => {
        state.teamLimitLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSubscriptionTier } = subscriptionTierSlice.actions;
export default subscriptionTierSlice.reducer;
